import React, { useEffect, useState, useCallback } from 'react';
import {
  Grid, Card, CardContent, Typography, Box, CircularProgress,
  Chip, Avatar, IconButton, Divider
} from '@mui/material';
import {
  MoreHoriz, TrendingUp, TrendingDown, People, CalendarMonth,
  Inventory2, AttachMoney, NavigateBefore, NavigateNext, Add,
  MedicalServices, Warning
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, PieChart, Pie, Cell
} from 'recharts';
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import {
  format, addMonths, subMonths, startOfMonth, endOfMonth,
  eachDayOfInterval, isSameDay, isToday, getDay
} from 'date-fns';

// ── Mini Calendar ─────────────────────────────────────────────────────────────
const MiniCalendar = ({ appointments }: { appointments: any[] }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(new Date());
  const monthStart = startOfMonth(currentMonth);
  const monthEnd   = endOfMonth(currentMonth);
  const days       = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startPad   = getDay(monthStart);
  const dayNames   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

  const hasAppt = (day: Date) =>
    appointments.some(a => isSameDay(new Date(a.appointmentDate), day));

  const selectedAppts = appointments.filter(a =>
    isSameDay(new Date(a.appointmentDate), selectedDay)
  );

  return (
    <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3, height: '100%' }}>
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
            {format(currentMonth, 'MMMM yyyy')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {[
              { icon: <NavigateBefore sx={{ fontSize: 16 }} />, action: () => setCurrentMonth(subMonths(currentMonth, 1)) },
              { icon: <NavigateNext  sx={{ fontSize: 16 }} />, action: () => setCurrentMonth(addMonths(currentMonth, 1)) }
            ].map(({ icon, action }, i) => (
              <IconButton key={i} size="small" onClick={action}
                sx={{ width: 26, height: 26, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                {icon}
              </IconButton>
            ))}
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.3, mb: 0.5 }}>
          {dayNames.map(d => (
            <Typography key={d} sx={{ textAlign: 'center', fontSize: 10, fontWeight: 700, color: '#94a3b8', py: 0.3 }}>{d}</Typography>
          ))}
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.3 }}>
          {Array.from({ length: startPad }).map((_, i) => <Box key={`p${i}`} />)}
          {days.map(day => {
            const today    = isToday(day);
            const selected = isSameDay(day, selectedDay);
            const dot      = hasAppt(day);
            return (
              <Box key={day.toString()} onClick={() => setSelectedDay(day)}
                sx={{
                  textAlign: 'center', cursor: 'pointer', py: 0.6, borderRadius: 1.5, position: 'relative',
                  bgcolor: selected ? '#0d9488' : today ? '#f0fdfa' : 'transparent',
                  border: today && !selected ? '1px solid #0d9488' : '1px solid transparent',
                  '&:hover': { bgcolor: selected ? '#0f766e' : '#f0fdfa' }, transition: 'all 0.15s'
                }}>
                <Typography sx={{ fontSize: 12, fontWeight: selected || today ? 700 : 400, color: selected ? 'white' : today ? '#0d9488' : '#374151' }}>
                  {format(day, 'd')}
                </Typography>
                {dot && !selected && (
                  <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#0d9488', mx: 'auto', mt: 0.2 }} />
                )}
              </Box>
            );
          })}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
            {format(selectedDay, 'EEE, d MMM')}
          </Typography>
          <Chip label={`${selectedAppts.length} appts`} size="small"
            sx={{ bgcolor: '#f0fdfa', color: '#0d9488', fontSize: 10, height: 20 }} />
        </Box>

        {selectedAppts.length === 0 ? (
          <Typography sx={{ fontSize: 12, color: '#94a3b8', textAlign: 'center', py: 2 }}>
            No appointments
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 140, overflowY: 'auto' }}>
            {selectedAppts.slice(0, 4).map(a => (
              <Box key={a._id} sx={{ p: 1.2, bgcolor: '#f0fdfa', borderRadius: 2, borderLeft: '3px solid #0d9488' }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }} noWrap>
                  {a.patient?.name}
                </Typography>
                <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                  {a.timeSlot} · {a.type}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// ── Stat Card ─────────────────────────────────────────────────────────────────
const StatCard = ({ title, value, icon, color, bg, subtitle, loading }: any) => (
  <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3, '&:hover': { boxShadow: '0 4px 20px rgba(13,148,136,0.1)', transform: 'translateY(-2px)' }, transition: 'all 0.2s' }}>
    <CardContent sx={{ p: 2.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ width: 44, height: 44, borderRadius: 2.5, bgcolor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {React.cloneElement(icon, { sx: { color, fontSize: 22 } })}
        </Box>
        <IconButton size="small" sx={{ color: '#94a3b8', width: 28, height: 28 }}>
          <MoreHoriz fontSize="small" />
        </IconButton>
      </Box>
      {loading ? (
        <CircularProgress size={22} sx={{ color }} />
      ) : (
        <Typography sx={{ fontSize: 28, fontWeight: 800, color: '#0f172a', lineHeight: 1.1 }}>{value ?? '—'}</Typography>
      )}
      <Typography sx={{ fontSize: 13, color: '#64748b', mt: 0.5, fontWeight: 500 }}>{title}</Typography>
      {subtitle && <Typography sx={{ fontSize: 11, color: '#94a3b8', mt: 0.3 }}>{subtitle}</Typography>}
    </CardContent>
  </Card>
);

// ── Tooltip ───────────────────────────────────────────────────────────────────
const ChartTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{ bgcolor: 'white', border: '1px solid #e2e8f0', borderRadius: 2, p: 1.5, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0f172a', mb: 0.5 }}>{label}</Typography>
      {payload.map((p: any) => (
        <Typography key={p.name} sx={{ fontSize: 12, color: p.color }}>
          {p.name}: <strong>{typeof p.value === 'number' && p.value > 1000 ? `₹${p.value.toLocaleString()}` : p.value}</strong>
        </Typography>
      ))}
    </Box>
  );
};

const PIE_COLORS = ['#0d9488','#14b8a6','#5eead4','#99f6e4'];

// ── Main Dashboard ────────────────────────────────────────────────────────────
const Dashboard = () => {
  const { user } = useAuth();
  const [stats,        setStats]        = useState<any>(null);
  const [todayAppts,   setTodayAppts]   = useState<any[]>([]);
  const [allAppts,     setAllAppts]     = useState<any[]>([]);
  const [doctors,      setDoctors]      = useState<any[]>([]);
  const [lowStock,     setLowStock]     = useState<any[]>([]);
  const [revenueData,  setRevenueData]  = useState<any[]>([]);
  const [patientData,  setPatientData]  = useState<any[]>([]);
  const [deptPie,      setDeptPie]      = useState<any[]>([]);
  const [loading,      setLoading]      = useState(true);

  const load = useCallback(async () => {
    try {
      const hospitalId = user?.hospital?._id;
      const calls: Promise<any>[] = [
        api.get('/appointments/today'),
        api.get('/appointments', { params: { limit: 200 } }),
        api.get('/doctors', { params: { limit: 6 } }),
        api.get('/inventory/alerts/low-stock').catch(() => ({ data: { data: [] } })),
        api.get('/invoices/report/revenue').catch(() => ({ data: { data: [] } })),
      ];
      if (hospitalId) calls.push(api.get(`/hospitals/${hospitalId}/stats`));

      const results = await Promise.all(calls);
      const [todayRes, allRes, docRes, lowRes, revRes, statsRes] = results;

      setTodayAppts(todayRes.data.data   || []);
      setAllAppts(allRes.data.data       || []);
      setDoctors(docRes.data.data        || []);
      setLowStock(lowRes.data.data       || []);
      if (statsRes) setStats(statsRes.data.data || {});

      // Build revenue chart from real invoice data (last 7 days)
      const revRaw: any[] = revRes.data.data || [];
      const last7 = Array.from({ length: 7 }, (_, i) => {
        const d = new Date();
        d.setDate(d.getDate() - (6 - i));
        const key  = format(d, 'yyyy-MM-dd');
        const found = revRaw.find((r: any) => r._id === key);
        return { day: format(d, 'EEE'), Income: found?.revenue || 0, Expense: Math.round((found?.revenue || 0) * 0.35) };
      });
      setRevenueData(last7);

      // Patient appointments by day for bar chart
      const allA: any[] = allRes.data.data || [];
      const last7days = Array.from({ length: 7 }, (_, i) => {
        const d = new Date();
        d.setDate(d.getDate() - (6 - i));
        const dayAppts = allA.filter(a => isSameDay(new Date(a.appointmentDate), d));
        return {
          date: format(d, 'd MMM'),
          Consultation: dayAppts.filter((a: any) => a.type === 'consultation').length,
          'Follow-up':  dayAppts.filter((a: any) => a.type === 'follow-up').length,
          Emergency:    dayAppts.filter((a: any) => a.type === 'emergency').length,
        };
      });
      setPatientData(last7days);

      // Dept breakdown from today's appointments
      const deptCount: Record<string, number> = {};
      (todayRes.data.data || []).forEach((a: any) => {
        const n = a.department?.name || 'General';
        deptCount[n] = (deptCount[n] || 0) + 1;
      });
      const pie = Object.entries(deptCount).map(([name, value]) => ({ name, value }));
      setDeptPie(pie.length > 0 ? pie : [{ name: 'No Data', value: 1 }]);
    } catch (e) {
      console.error('Dashboard load error:', e);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const statusColors: Record<string, any> = {
    scheduled:    { color: '#3b82f6', bg: '#eff6ff' },
    confirmed:    { color: '#10b981', bg: '#ecfdf5' },
    'checked-in': { color: '#8b5cf6', bg: '#f5f3ff' },
    'in-progress':{ color: '#f59e0b', bg: '#fffbeb' },
    completed:    { color: '#059669', bg: '#ecfdf5' },
    cancelled:    { color: '#ef4444', bg: '#fef2f2' },
    'no-show':    { color: '#94a3b8', bg: '#f8fafc' },
  };

  const totalPiePatients = deptPie.reduce((s, d) => s + d.value, 0);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>
          Good {new Date().getHours() < 12 ? 'Morning' : new Date().getHours() < 17 ? 'Afternoon' : 'Evening'},{' '}
          {user?.name?.split(' ')[0]} 👋
        </Typography>
        <Typography sx={{ color: '#64748b', fontSize: 14, mt: 0.3 }}>
          {format(new Date(), 'EEEE, d MMMM yyyy')} · Hospital Management Dashboard
        </Typography>
      </Box>

      {/* Stat Cards */}
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={6} xl={3}>
          <StatCard loading={loading} title="Total Patients" icon={<People />} color="#0d9488" bg="#f0fdfa"
            value={stats?.totalPatients?.toLocaleString()}
            subtitle={`${todayAppts.length} appointments today`} />
        </Grid>
        <Grid item xs={12} sm={6} xl={3}>
          <StatCard loading={loading} title="Total Revenue" icon={<AttachMoney />} color="#6366f1" bg="#eef2ff"
            value={stats?.totalRevenue ? `₹${(stats.totalRevenue / 1000).toFixed(1)}K` : undefined}
            subtitle="All time collected" />
        </Grid>
        <Grid item xs={12} sm={6} xl={3}>
          <StatCard loading={loading} title="Today's Appointments" icon={<CalendarMonth />} color="#f59e0b" bg="#fffbeb"
            value={stats?.todayAppointments ?? todayAppts.length}
            subtitle={`${todayAppts.filter(a => a.status === 'completed').length} completed`} />
        </Grid>
        <Grid item xs={12} sm={6} xl={3}>
          <StatCard loading={loading} title="Low Stock Alerts" icon={<Warning />} color="#ef4444" bg="#fef2f2"
            value={lowStock.length}
            subtitle="Items need reorder" />
        </Grid>
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>

        {/* Appointment bar chart (real data) */}
        <Grid item xs={12} lg={5}>
          <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3 }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 15 }}>Appointments Overview</Typography>
                  <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>Last 7 days by type</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  {[{ l: 'Consultation', c: '#0d9488' }, { l: 'Follow-up', c: '#14b8a6' }, { l: 'Emergency', c: '#f59e0b' }].map(x => (
                    <Box key={x.l} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: x.c }} />
                      <Typography sx={{ fontSize: 10, color: '#64748b' }}>{x.l}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: '#0d9488' }} /></Box>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={patientData} barGap={2} barSize={9}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip content={<ChartTooltip />} />
                    <Bar dataKey="Consultation" fill="#0d9488"  radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Follow-up"    fill="#5eead4" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Emergency"    fill="#f59e0b" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Revenue area chart (real data) */}
        <Grid item xs={12} lg={4}>
          <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3 }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 15 }}>Revenue</Typography>
                  <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>Last 7 days</Typography>
                </Box>
              </Box>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: '#0d9488' }} /></Box>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={revenueData}>
                    <defs>
                      <linearGradient id="gIncome" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor="#0d9488" stopOpacity={0.18} />
                        <stop offset="95%" stopColor="#0d9488" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gExpense" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor="#5eead4" stopOpacity={0.18} />
                        <stop offset="95%" stopColor="#5eead4" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <Tooltip content={<ChartTooltip />} />
                    <Area type="monotone" dataKey="Income"  stroke="#0d9488" strokeWidth={2.5} fill="url(#gIncome)"  dot={false} />
                    <Area type="monotone" dataKey="Expense" stroke="#5eead4" strokeWidth={2.5} fill="url(#gExpense)" dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Calendar (real appointments) */}
        <Grid item xs={12} lg={3}>
          <MiniCalendar appointments={allAppts} />
        </Grid>
      </Grid>

      {/* Bottom Row */}
      <Grid container spacing={2.5}>

        {/* Dept Pie (today's data) */}
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3 }}>
            <CardContent sx={{ p: 2.5 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 0.3 }}>Appointments Today</Typography>
              <Typography sx={{ fontSize: 12, color: '#94a3b8', mb: 2 }}>by Department</Typography>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: '#0d9488' }} /></Box>
              ) : (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'center', position: 'relative' }}>
                    <PieChart width={180} height={180}>
                      <Pie data={deptPie} cx={85} cy={85} innerRadius={52} outerRadius={82} paddingAngle={3} dataKey="value" stroke="none">
                        {deptPie.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                    <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                      <Typography sx={{ fontSize: 24, fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>{totalPiePatients}</Typography>
                      <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>Total</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ mt: 1.5 }}>
                    {deptPie.slice(0, 5).map((d, i) => (
                      <Box key={d.name} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.6 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: PIE_COLORS[i % PIE_COLORS.length] }} />
                          <Typography sx={{ fontSize: 12, color: '#64748b' }} noWrap>{d.name}</Typography>
                        </Box>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{d.value}</Typography>
                      </Box>
                    ))}
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Doctors on duty (real data) */}
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3, height: '100%' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 15 }}>Doctors</Typography>
                  <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>Active roster</Typography>
                </Box>
                <Chip label={`${doctors.length} total`} size="small" sx={{ bgcolor: '#f0fdfa', color: '#0d9488', fontSize: 10 }} />
              </Box>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: '#0d9488' }} /></Box>
              ) : doctors.length === 0 ? (
                <Typography sx={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', py: 4 }}>No doctors registered yet</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {doctors.slice(0, 5).map((doc, i) => {
                    const todayDay = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
                    const daySchedule = doc.weeklySchedule?.find((s: any) => s.day === todayDay);
                    const available = daySchedule?.isWorking && doc.acceptingNewPatients;
                    return (
                      <Box key={doc._id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ width: 38, height: 38, bgcolor: i % 2 === 0 ? '#f0fdfa' : '#eef2ff', color: i % 2 === 0 ? '#0d9488' : '#6366f1', fontWeight: 800, fontSize: 13 }}>
                          {doc.user?.name?.split(' ').slice(-2).map((n: string) => n[0]).join('')}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }} noWrap>
                            Dr. {doc.user?.name}
                          </Typography>
                          <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>
                            {doc.specialization}
                            {daySchedule?.isWorking ? ` · ${daySchedule.startTime}–${daySchedule.endTime}` : ''}
                          </Typography>
                        </Box>
                        <Chip label={available ? 'Available' : 'Off Today'} size="small"
                          sx={{ fontSize: 10, height: 20, bgcolor: available ? '#dcfce7' : '#fee2e2', color: available ? '#15803d' : '#b91c1c' }} />
                      </Box>
                    );
                  })}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Today's Queue (real data) */}
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3, height: '100%' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 15 }}>Today's Queue</Typography>
                  <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>
                    {todayAppts.filter(a => a.status === 'completed').length} of {todayAppts.length} done
                  </Typography>
                </Box>
              </Box>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: '#0d9488' }} /></Box>
              ) : todayAppts.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CalendarMonth sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                  <Typography sx={{ color: '#94a3b8', fontSize: 13 }}>No appointments today</Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 270, overflowY: 'auto' }}>
                  {todayAppts.map(a => {
                    const sc = statusColors[a.status] || statusColors.scheduled;
                    return (
                      <Box key={a._id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #f1f5f9' }}>
                        <Box sx={{ width: 30, height: 30, borderRadius: '50%', bgcolor: '#0d9488', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <Typography sx={{ color: 'white', fontWeight: 800, fontSize: 10 }}>#{a.tokenNumber}</Typography>
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }} noWrap>{a.patient?.name}</Typography>
                          <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>
                            {a.timeSlot} · Dr.{a.doctor?.user?.name?.split(' ').slice(-1)[0]}
                          </Typography>
                        </Box>
                        <Chip label={a.status} size="small"
                          sx={{ bgcolor: sc.bg, color: sc.color, fontSize: 10, textTransform: 'capitalize', height: 18 }} />
                      </Box>
                    );
                  })}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
